import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, TrendingUp, TrendingDown, Minus, Bell } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API, scoreColor } from "../../lib/operatorWalk";

function DeltaIcon({ delta }) {
  if (delta === "up") return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (delta === "down") return <TrendingDown className="w-4 h-4 text-red-600" />;
  return <Minus className="w-4 h-4 text-ink-muted" />;
}

export default function AdminOverview({ refreshKey }) {
  const { authHeader } = usePortalAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/admin/ow/overview`, { headers: authHeader });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center text-ink-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading overview
      </div>
    );
  }
  if (!data) return <p className="text-ink-muted text-sm">Could not load overview.</p>;

  const { counts, rows, reminders } = data;

  return (
    <div className="space-y-8" data-testid="admin-overview">
      {/* counts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-200 border border-brand-200">
        {[
          { label: "Active clients", value: counts.active },
          { label: "Paused", value: counts.paused },
          { label: "Churned", value: counts.churned },
          { label: "Total clients", value: counts.total },
        ].map((s) => (
          <div key={s.label} className="bg-white p-5" data-testid={`overview-count-${s.label}`}>
            <div className="font-serif text-4xl text-brand-700">{s.value}</div>
            <div className="text-xs uppercase tracking-[0.18em] text-ink-muted mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* reminders */}
      <div className="border border-brand-200 bg-white" data-testid="overview-reminders">
        <div className="px-5 py-4 border-b border-brand-100 flex items-center gap-2">
          <Bell className="w-5 h-5 text-brand-500" />
          <h3 className="font-serif text-xl text-brand-700">Due for a visit this month</h3>
        </div>
        {reminders.length ? (
          <ul className="divide-y divide-brand-100">
            {reminders.map((r, i) => (
              <li key={`${r.location_id}-${i}`} className="px-5 py-3 flex items-center justify-between gap-4 text-sm" data-testid={`reminder-${r.location_id}`}>
                <span className="text-brand-700 font-medium">
                  {r.company_name} <span className="text-ink-muted font-normal">. {r.location_name}</span>
                </span>
                <span className="text-ink-muted text-xs">{r.visit_day}{r.visit_window ? `, ${r.visit_window}` : ""}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-4 text-sm text-ink-muted">Every active location has a report this month. Nothing due.</p>
        )}
      </div>

      {/* table */}
      <div className="border border-brand-200 bg-white overflow-x-auto" data-testid="overview-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-100 text-left text-[10px] uppercase tracking-[0.18em] text-brand-600">
              <th className="px-5 py-3 font-medium">Client</th>
              <th className="px-5 py-3 font-medium">Location</th>
              <th className="px-5 py-3 font-medium">Latest score</th>
              <th className="px-5 py-3 font-medium">Trend</th>
              <th className="px-5 py-3 font-medium">Last visit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-100">
            {rows.map((r, i) => (
              <tr key={`${r.location_id}-${i}`} data-testid={`overview-row-${r.location_id}`}>
                <td className="px-5 py-3 text-brand-700 font-medium">{r.company_name}</td>
                <td className="px-5 py-3 text-ink-soft">{r.location_name}</td>
                <td className="px-5 py-3">
                  {r.latest_score != null ? (
                    <span className="font-serif text-lg" style={{ color: scoreColor(r.latest_score) }}>{r.latest_score}</span>
                  ) : (
                    <span className="text-ink-muted">No report</span>
                  )}
                </td>
                <td className="px-5 py-3"><DeltaIcon delta={r.delta} /></td>
                <td className="px-5 py-3 text-ink-muted">{r.last_visit || r.last_month_label || "."}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-6 text-center text-ink-muted">No active client locations yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
